import type {
  ExpressionSpecification,
  FilterSpecification,
  GeoJSONSourceSpecification,
  LineLayerSpecification,
} from 'maplibre-gl';
import type { FeatureCollection } from 'geojson';

import type { RouteVariantKey } from '@/types/routes';

export const ROUTES_GIS_SOURCE_ID = 'routes-gis';
export const ROUTES_ITEKO_SOURCE_ID = 'routes-iteko';
export const ROUTES_BARRIER_AWARE_SOURCE_ID = 'routes-barrier-aware';

export const ROUTES_GIS_LAYER_ID = 'routes-gis-line';
export const ROUTES_GIS_HIGHLIGHT_LAYER_ID = 'routes-gis-highlight';
export const ROUTES_GIS_HOVERED_LAYER_ID = 'routes-gis-hovered';
export const ROUTES_ITEKO_LAYER_ID = 'routes-iteko-line';
export const ROUTES_ITEKO_HIGHLIGHT_LAYER_ID = 'routes-iteko-highlight';
export const ROUTES_ITEKO_HOVERED_LAYER_ID = 'routes-iteko-hovered';
export const ROUTES_BARRIER_AWARE_LAYER_ID = 'routes-barrier-aware-line';
export const ROUTES_BARRIER_AWARE_HIGHLIGHT_LAYER_ID = 'routes-barrier-aware-highlight';
export const ROUTES_BARRIER_AWARE_HOVERED_LAYER_ID = 'routes-barrier-aware-hovered';

/**
 * Per-variant palette — must match the swatch colours used in
 * `MapLegend.tsx` so the user sees the same colour on map and legend.
 */
export const VARIANT_PALETTE: Record<
  RouteVariantKey,
  { stroke: string; highlight: string }
> = {
  gis: { stroke: '#d4a017', highlight: '#b57d00' },
  iteko: { stroke: '#722ed1', highlight: '#531dab' },
  barrier_aware: { stroke: '#389e0d', highlight: '#237804' },
};

const LINE_WIDTH: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  6, 2,
  10, 3,
  14, 4,
];

const HIGHLIGHT_WIDTH: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  6, 4,
  10, 6,
  14, 8,
];

interface VariantSourceSpec {
  readonly sourceId: string;
  readonly lineLayerId: string;
  readonly highlightLayerId: string;
  readonly hoveredLayerId: string;
}

export const VARIANT_LAYER_SPECS: Record<RouteVariantKey, VariantSourceSpec> = {
  gis: {
    sourceId: ROUTES_GIS_SOURCE_ID,
    lineLayerId: ROUTES_GIS_LAYER_ID,
    highlightLayerId: ROUTES_GIS_HIGHLIGHT_LAYER_ID,
    hoveredLayerId: ROUTES_GIS_HOVERED_LAYER_ID,
  },
  iteko: {
    sourceId: ROUTES_ITEKO_SOURCE_ID,
    lineLayerId: ROUTES_ITEKO_LAYER_ID,
    highlightLayerId: ROUTES_ITEKO_HIGHLIGHT_LAYER_ID,
    hoveredLayerId: ROUTES_ITEKO_HOVERED_LAYER_ID,
  },
  barrier_aware: {
    sourceId: ROUTES_BARRIER_AWARE_SOURCE_ID,
    lineLayerId: ROUTES_BARRIER_AWARE_LAYER_ID,
    highlightLayerId: ROUTES_BARRIER_AWARE_HIGHLIGHT_LAYER_ID,
    hoveredLayerId: ROUTES_BARRIER_AWARE_HOVERED_LAYER_ID,
  },
};

export function buildEmptyCollection(): FeatureCollection {
  return { type: 'FeatureCollection', features: [] };
}

export function buildVariantSource(): GeoJSONSourceSpecification {
  return {
    type: 'geojson',
    data: buildEmptyCollection(),
    promoteId: 'route_id',
  };
}

export function buildVariantLineLayer(
  variant: RouteVariantKey,
): LineLayerSpecification {
  const spec = VARIANT_LAYER_SPECS[variant];
  const palette = VARIANT_PALETTE[variant];
  return {
    id: spec.lineLayerId,
    type: 'line',
    source: spec.sourceId,
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': palette.stroke,
      'line-width': LINE_WIDTH,
      'line-opacity': 0.85,
    },
  };
}

export function buildVariantHighlightLayer(
  variant: RouteVariantKey,
  focusedRouteId: string | null,
): LineLayerSpecification {
  const spec = VARIANT_LAYER_SPECS[variant];
  const palette = VARIANT_PALETTE[variant];
  return {
    id: spec.highlightLayerId,
    type: 'line',
    source: spec.sourceId,
    filter: buildFocusedFilter(focusedRouteId),
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': palette.highlight,
      'line-width': HIGHLIGHT_WIDTH,
      'line-opacity': 0.95,
    },
  };
}

export function buildFocusedFilter(
  focusedRouteId: string | null,
): FilterSpecification {
  if (focusedRouteId === null) {
    return ['==', ['get', 'route_id'], '__none__'];
  }
  return ['==', ['get', 'route_id'], focusedRouteId];
}

const HOVERED_WIDTH: ExpressionSpecification = [
  'interpolate', ['linear'], ['zoom'],
  6, 3,
  10, 4.5,
  14, 6,
];

/**
 * Slightly thicker, slightly darker line drawn on top of the base
 * variant layer when the user hovers a route. Distinct from
 * `highlight` (the focus layer) so a hovered-but-not-focused route
 * still gets visual feedback.
 */
export function buildVariantHoveredLayer(
  variant: RouteVariantKey,
): LineLayerSpecification {
  const spec = VARIANT_LAYER_SPECS[variant];
  const palette = VARIANT_PALETTE[variant];
  return {
    id: spec.hoveredLayerId,
    type: 'line',
    source: spec.sourceId,
    filter: buildFocusedFilter(null),
    layout: { 'line-cap': 'round', 'line-join': 'round' },
    paint: {
      'line-color': palette.highlight,
      'line-width': HOVERED_WIDTH,
      'line-opacity': 0.7,
    },
  };
}
